import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { listify } from 'radash';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import {
  Attendance,
  AttendanceStatus,
  showOnSessionAttendance,
} from '../../../../models/attendance.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolAttendanceService } from '../../../../services/attendance.school.service';
import * as teacherActions from '../../../../state/actions/teacher.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as teacherSelector from '../../../../state/selectors/teacher.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { LoadingBoxComponent } from '../../../components/loaders/loading-box/loading-box.component';

@Component({
  selector: 'app-dlg-session-attendance',
  standalone: true,
  imports: [AsyncPipe, BtnActionComponent, LoadingBoxComponent],
  templateUrl: './dlg-session-attendance.component.html',
  styleUrl: './dlg-session-attendance.component.css',
})
export class DlgSessionAttendanceComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgSessionAttendanceComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  store: Store<AppState> = inject(Store);
  attendanceService = inject(SchoolAttendanceService);
  private logger = inject(NGXLogger);
  private toastr = inject(ToastrService);

  title: string = 'Update Attendance';
  description: string = 'Update Session Attendance';
  action: string = 'Update';

  updateAttendance: { [key: string]: Attendance } = {}; // Attendance of selected students that teacher wants to update
  currAttendance: Map<string, Attendance> = new Map(); // Attendance of all students updated (on initialization) with the current status from db

  isWorking$: Observable<boolean> = new Observable();

  allStudents: SchoolMember[] | null | undefined; // active students of the grade
  students: SchoolMember[] | null | undefined; // those listed for this session
  grade: Grade | null | undefined;

  attendanceStatus = AttendanceStatus;

  exit$ = new Subject<void>();

  constructor() {
    this.title = this.data.title ?? 'Update Attendance';
    this.description = this.data.description ?? 'Update Session Attendance';
    this.action = this.data.action ?? 'Update';

    this.store
      .select(
        teacherSelector.getStudentsByGrade({
          groupId: this.data.groupId,
          gradeId: this.data.gradeId,
          academicYear: this.data.academicYear,
          status: [MembershipStatus.active],
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((students) => {
        this.allStudents = students;
        this.getAttendanceBySession();
      });

    this.store
      .select(
        gradeSelector.getGradeById({
          gradeId: this.data.gradeId,
          groupId: this.data.groupId,
          academicYear: this.data.academicYear,
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((grade) => {
        this.grade = grade;
      });
  }

  async ngOnInit() {
    this.isWorking$ = of(true);

    if ((this.allStudents ?? []).length == 0 && this.grade) {
      this.store.dispatch(
        teacherActions.getStudents({
          source: 'DlgSessionAttendanceComponent',
          groupId: this.grade?.groupId!,
          gradeId: this.grade?.id,
          status: [MembershipStatus.active],
        }),
      );
    }
  }

  getAttendanceBySession() {
    this.logger.debug(
      `DlgSessionAttendanceComponent :: getAttendanceBySession: ${this.data.groupId} ${this.data.sessionId}`,
    );
    this.attendanceService
      .getAttendanceBySessionId(this.data.groupId, this.data.sessionId)
      .then((dbAttendanceList) => {
        this.logger.debug(
          `DlgSessionAttendanceComponent :: getAttendanceBySession: ${dbAttendanceList.length} record(s)`,
        );
        // Students who joined the grade after this session aren't listed
        const recorded = new Set(dbAttendanceList.map((a) => a.studentId));
        this.students = (this.allStudents ?? []).filter((student) =>
          showOnSessionAttendance({
            hasRecord: recorded.has(student.memberId!),
            sessionStart: this.data.sessionStart,
            gradeJoinedAt: student.gradeJoinedAt,
            created: student.created,
          }),
        );
        this.students.forEach((student) => {
          const attendance = dbAttendanceList.find(
            (a) => a.studentId === student.memberId,
          );

          if (attendance == undefined) {
            this.updateAttendance[student.memberId!] = {
              sessionId: this.data.sessionId,
              groupId: this.data.groupId,
              studentId: student.memberId,
              gradeId: this.grade?.id,
              attendanceStatus: AttendanceStatus.absent,
              academicYear: this.data.academicYear,
            } as Attendance;
          }

          this.currAttendance.set(
            student.memberId,
            attendance ??
              ({
                sessionId: this.data.sessionId,
                groupId: this.data.groupId,
                studentId: student.memberId,
                gradeId: this.grade?.id,
                attendanceStatus: AttendanceStatus.absent,
                academicYear: this.data.academicYear,
              } as Attendance),
          );
        });
      })
      .catch((error) => {
        this.logger.error(
          'DlgSessionAttendanceComponent :: getAttendanceBySession failed',
          error,
        );
      })
      .finally(() => {
        this.isWorking$ = of(false);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onToggled(student: SchoolMember) {
    // Toggle from what is shown (pending change, else stored status).
    const current = this.getAttendanceStatus(student.memberId);
    const next =
      current === AttendanceStatus.present
        ? AttendanceStatus.absent
        : AttendanceStatus.present;
    this.logger.debug(
      `DlgSessionAttendanceComponent :: onToggled: ${student.memberId} ${current} -> ${next}`,
    );
    this.updateAttendance[student.memberId!] = {
      sessionId: this.data.sessionId,
      groupId: this.data.groupId,
      studentId: student.memberId,
      gradeId: this.grade?.id,
      attendanceStatus: next,
      academicYear: this.data.academicYear,
    } as Attendance;
  }

  getAttendanceStatus(studentId: string | undefined): string {
    if (studentId === undefined) {
      return AttendanceStatus.absent;
    }
    return (
      this.updateAttendance[studentId]?.attendanceStatus ??
      this.currAttendance.get(studentId)?.attendanceStatus ??
      AttendanceStatus.absent
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    this.isWorking$ = of(true);
    const entries = Object.entries(this.updateAttendance);
    this.logger.debug(
      `DlgSessionAttendanceComponent :: submit: ${entries.length} record(s) for session ${this.data.sessionId}`,
    );
    try {
      // Wait for every write; close only when all succeeded.
      await Promise.all(
        entries.map(([studentId, attendance]) => {
          const existing = this.currAttendance.get(studentId);
          return this.attendanceService.update(
            this.data.groupId!,
            `${this.data.sessionId}_${studentId}`,
            {
              ...attendance,
              // keep the original creation time of stored records
              created: (existing as any)?.created ?? Timestamp.now(),
              modified: Timestamp.now(),
            } as Attendance,
          );
        }),
      );
      this.dialogRef.close(true);
    } catch (error) {
      this.logger.error('DlgSessionAttendanceComponent :: submit failed', error);
      this.toastr.error(
        'Some attendance records could not be saved. Please try again.',
        'Error Saving Attendance',
      );
    } finally {
      this.isWorking$ = of(false);
    }
  }
}
